import { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';

import { defaultPageRowOptions, defaultRowOptions, paginatorLinks, showingEntries, SkeletonbodyTemplate, Skeletonitems } from '../../appconfig/Settings';
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import { getAllDetails, getDetailsFromId, postAllData, deleteData } from '../../service/ProductService';
import { LIST_PARTIES, GET_PARTY_BY_ID, CREATE_PARTY, UPDATE_PARTY, DELETE_PARTY } from '../../config/ApiConstant';

export const Parties = () => {
    document.title = 'Parties | Karan Enterprise';

    const toast = useRef<any>(null);

    const [globalFilter, setGlobalFilter] = useState<any>(null);
    const [partyList, setPartyList] = useState<any>([]);
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [partyModal, setPartyModal] = useState<boolean>(false);
    const [partyData, setPartyData] = useState<any>({});
    const [editId, setEditId] = useState<any>(null);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>({});

    useEffect(() => {
        getPartyList();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const getPartyList = async () => {
        try {
            setPageLoad(false);
            const response = await getAllDetails(LIST_PARTIES);
            if (response) {
                setPartyList(response?.data || []);
                setPageLoad(true);
            }
        } catch (error) {
            setPageLoad(false);
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: 'Something went wrong, Please try again.'
            });
        }
    };

    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                </span>
            </>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <>
                <Button label="Add Party" icon="pi pi-plus" className="p-button-primary" onClick={() => openAddModal()} />
            </>
        );
    };

    const openAddModal = () => {
        setPartyData({});
        setEditId(null);
        setErrors({});
        setPartyModal(true);
    };

    const openEditModal = async (rowData: any) => {
        try {
            setEditId(rowData.id);
            const response = await getDetailsFromId(GET_PARTY_BY_ID, 'id', rowData.id);
            if (response) {
                setPartyData(response);
                setPartyModal(true);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: 'Failed to fetch party details'
            });
        }
    };

    const closeModal = () => {
        setPartyModal(false);
        setPartyData({});
        setEditId(null);
        setErrors({});
    };

    const onInputChange = (e: any, name: string) => {
        const val = e.target.value || '';
        setPartyData({ ...partyData, [name]: val });
    };

    const validateParty = () => {
        const errors: any = {};
        let isError = false;

        if (window.cn(partyData.party_name) || window.cb(partyData.party_name)) {
            errors.party_name = 'Please enter Party Name *';
            isError = true;
        }

        setErrors(errors);
        return !isError;
    };

    const onSubmit = async () => {
        try {
            if (!validateParty()) {
                return;
            }

            setSubmitLoading(true);

            if (editId !== null) {
                const requestData = {
                    id: editId,
                    party_name: partyData.party_name
                };
                const response = await postAllData(UPDATE_PARTY, requestData);
                if (response) {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: response.message || 'Party updated successfully'
                    });
                    closeModal();
                    getPartyList();
                }
            } else {
                const requestData = {
                    party_name: partyData.party_name
                };
                const response = await postAllData(CREATE_PARTY, requestData);
                if (response) {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: response.message || 'Party created successfully'
                    });
                    closeModal();
                    getPartyList();
                }
            }
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: error.response?.data?.message || 'Something went wrong'
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    const confirmDelete = (rowData: any) => {
        confirmDialog({
            message: 'Are you sure you want to delete this party?',
            header: 'Delete Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => handleDelete(rowData.id)
        });
    };

    const handleDelete = async (id: any) => {
        try {
            const response = await deleteData(DELETE_PARTY, id);
            if (response) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.message || 'Party deleted successfully'
                });
                getPartyList();
            }
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: error.response?.data?.message || 'Something went wrong'
            });
        }
    };

    const actionBodyTemplate = (rowData: any) => {
        return (
            <div className="actions">
                <Button icon="pi pi-eye" tooltip="View" tooltipOptions={{ position: 'top' }} className="p-button-rounded p-button-info mr-2" onClick={() => openEditModal(rowData)} />
                <Button icon="pi pi-pencil" tooltip="Edit" tooltipOptions={{ position: 'top' }} className="p-button-rounded p-button-success mr-2" onClick={() => openEditModal(rowData)} />
            </div>
        );
    };

    const nameTemplate = (rowData: any) => {
        return <span className="font-bold">{rowData.party_name}</span>;
    };

    return (
        <>
            <AppBreadcrumb currentMenu={'Parties'} pageName={'Parties'} pageTitle={'Parties'} />
            <div className="grid crud-demo expanded-table">
                <div className="col-12">
                    <div className="card">
                        <Toast ref={toast} />
                        <ConfirmDialog />
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
                        {pageLoad ? (
                            <>
                                <DataTable
                                    className="datatable-responsive"
                                    value={partyList}
                                    paginator={partyList.length >= 10 && true}
                                    globalFilter={globalFilter}
                                    rows={defaultRowOptions}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    paginatorTemplate={paginatorLinks}
                                    currentPageReportTemplate={showingEntries}
                                    emptyMessage="No Parties Found"
                                    showGridlines
                                    stripedRows
                                >
                                    <Column field="party_name" header="Party Name" sortable filter body={nameTemplate} />
                                    <Column field="action" header="Action" body={actionBodyTemplate} />
                                </DataTable>
                            </>
                        ) : (
                            <>
                                <DataTable value={Skeletonitems}>
                                    <Column field="party_name" header="Party Name" body={SkeletonbodyTemplate} />
                                    <Column field="action" header="Action" body={SkeletonbodyTemplate} />
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                visible={partyModal}
                style={{ width: '450px' }}
                header={editId !== null ? 'Edit Party' : 'Add Party'}
                modal
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary mr-2" onClick={closeModal} disabled={submitLoading} />
                        <Button label="Save" icon="pi pi-check" loading={submitLoading} onClick={onSubmit} />
                    </>
                }
                onHide={closeModal}
            >
                <div className="p-fluid">
                    <div className="field">
                        <label htmlFor="partyName">Party Name *</label>
                        <InputText id="partyName" value={partyData.party_name || ''} onChange={(e) => onInputChange(e, 'party_name')} className={errors.party_name ? 'p-invalid' : ''} placeholder="Enter Party Name" />
                        {errors.party_name && <small className="p-invalid-txt">{errors.party_name}</small>}
                    </div>
                </div>
            </Dialog>
        </>
    );
};
